import ContentLayout from "@/components/layout/ContentLayout";
import OverviewAgenda from "@/components/overview-agenda";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { CalendarEvent } from "@/components/ui/full-calendar";
import { useAuth } from "@/contexts/AuthContext";
import { ROUTE } from "@/routes/constants";
import {
  getAssignmentListByStudent,
  querySurveyV2,
  clearSurvey,
} from "@/services/generatedAPIs";
import { AssignmentModel, GetAssignmentListResponse } from "@/services/generatedAPIs.schemas";
import { addHours, differenceInDays, format } from "date-fns";
import { ReactNode, useEffect, useState, useRef } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { NavLink } from "react-router";
import { useSubjectList } from "@/hooks/use-subject-list";
import { getSubjectDisplayName } from "@/hooks/use-subject-name";
import styles from "@/components/css/OverviewPage.module.css";
import { isEN } from "@/utils/i18n";

import Survey from "@/components/survey"; // 17 items
import Survey2 from "@/components/survey2"; // 6 items
import Survey3 from "@/components/survey3"; // 10 items
import Survey4 from "@/components/survey4"; // 12 items
import Survey5 from "@/components/survey5";

const OVERVIEW_DEFAULT_ACCENT = "#9CEA4F";

type OverviewTheme = "orange" | "green" | "blue" | "pink";

function readOverviewTheme(): OverviewTheme {
  if (typeof document === "undefined") return "orange";
  const t = document.documentElement.getAttribute("data-theme") as OverviewTheme | null;
  return t && (["orange", "green", "blue", "pink"] as const).includes(t) ? t : "orange";
}

function useOverviewAccent(): { accent: string; panelBg: string; arrow: string } {
  const [theme, setTheme] = useState<OverviewTheme>(readOverviewTheme);
  useEffect(() => {
    const obs = new MutationObserver(() => setTheme(readOverviewTheme()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  if (theme === "orange") {
    return {
      accent: OVERVIEW_DEFAULT_ACCENT,
      panelBg: "#D0FFCD",
      // 課業輪播左右箭頭：默認主題綠底（其餘主題跟隨 primary 主題色），箭頭符號統一黑色
      arrow: "#7DD02A",
    };
  }
  return { accent: "var(--primary)", panelBg: "#FEFEFE", arrow: "var(--primary)" };
}

function ProgressRing({
  percent,
  color,
  size = 84,
  stroke = 9,
}: {
  percent: number;
  color: string;
  size?: number;
  stroke?: number;
}) {
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, percent));
  const offset = circumference - (clamped / 100) * circumference;
  return (
    <div className="relative h-full w-full">
      <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E6E6E6" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {/* 环形中心数值与进度弧同色：默认主题为 #9CEA4F，其余主题跟随 var(--primary) */}
        <span className="text-sm font-bold tabular-nums xl:text-base 2xl:text-lg" style={{ color }}>{clamped}%</span>
      </div>
    </div>
  );
}

type CarouselDataType = {
  /** 題型序號（全局連續，作卡片左上角小標籤） */
  ordinal: number;
  /** 題型名稱（來自作業 topicList，粗體顯示） */
  title: string;
  /** 該題型已完成題數 / 總題數 → 環形完成進度 */
  done: number;
  total: number;
  assignments: AssignmentModel[];
};

function OverviewPage({ t, i18n, ...props }: WithTranslation) {
  const { getCurrentUser } = useAuth();
  const { accent, panelBg, arrow } = useOverviewAccent();
  const { subjectInfos } = useSubjectList();
  const [carouselData, setCarouselData] = useState<CarouselDataType[]>([]);
  const [calendarData, setCalendarData] = useState<CalendarEvent[]>([]);
  const [surveyIndex, setSurveyIndex] = useState<number | undefined>();
  const [carApi, setCarApi] = useState<CarouselApi>();
  const [assignmentRaw, setAssignmentRaw] = useState<GetAssignmentListResponse | null>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Prevent double fetching in StrictMode
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    getAssignmentListByStudent({ studentId: getCurrentUser()!.id, type: "homework" })
      .then((rsp) => {
        if (rsp.code === 200 && rsp.data) {
          setAssignmentRaw(rsp.data);
        }
      })
      .catch((e) => {
        console.error("Error fetching assignment list", e);
      });
  }, []);

  useEffect(() => {
    if (!assignmentRaw) return;

    const assignmentList = assignmentRaw.assignmentList;
    const statsList = assignmentRaw.assignmentStatsList;
    const cards: CarouselDataType[] = [];
    const newCalendarData: CalendarEvent[] = [];
    let ordinal = 0;

    for (let i = 0; i < assignmentList.length; ++i) {
      const assignment = assignmentList[i];
      const fallbackName = assignment.subject || t("common.loading");
      const subjectName = getSubjectDisplayName(
        subjectInfos,
        assignment.subjectId,
        fallbackName,
        i18n.language
      ) ?? fallbackName;
      const stat = statsList[i];

      // 方案B: dev-cpp 卡片模型 —— 逐作业一张卡(连续题型序号), 不排除已完成, 无随心练占位卡
      cards.push({
        ordinal: ++ordinal,
        title: assignment.assignment.title || assignment.topicList.join("、") || subjectName,
        done: stat?.submittedCount ?? 0,
        total: stat?.totalCount ?? 0,
        assignments: [assignment],
      });

      // Add all assignments to calendar (both completed and incomplete)
      const dueDate = new Date(assignment.assignment.dueDate!);
      newCalendarData.push({
        id: i.toString(),
        start: dueDate,
        end: addHours(dueDate, 1),
        title: subjectName + ":" + assignment.topicList.join(","),
        data: assignmentList[i],
        color: "pink",
      });
    }

    setCarouselData(cards);
    setCalendarData(newCalendarData);
  }, [assignmentRaw, subjectInfos, i18n, t]);

  useEffect(() => {
    if (import.meta.env.DEV) return;
    const setup = async () => {
      let rsp = await querySurveyV2({
        userId: getCurrentUser()!.id,
        surveyId: 1,
      });
      if (rsp.code === 80005000) {
        await clearSurvey({ userId: getCurrentUser()!.id, surveyId: 1 });
      } else if (rsp.code === 200 && rsp.data!.hasRecord === false) {
        setSurveyIndex(1);
      } else {
        rsp = await querySurveyV2({
          userId: getCurrentUser()!.id,
          surveyId: 2,
        });
        if (rsp.code === 80005000) {
          await clearSurvey({ userId: getCurrentUser()!.id, surveyId: 2 });
        } else if (rsp.code === 200 && rsp.data!.hasRecord === false) {
          setSurveyIndex(2);
        } else if (differenceInDays(new Date().toISOString(), (rsp.data!.submitTime! + 'Z')) > 1) {
          rsp = await querySurveyV2({
            userId: getCurrentUser()!.id,
            surveyId: 3,
          });
          if (rsp.code === 80005000) {
            await clearSurvey({ userId: getCurrentUser()!.id, surveyId: 3 });
          } else if (rsp.code === 200 && rsp.data!.hasRecord === false) {
            setSurveyIndex(3);
          } else {
            rsp = await querySurveyV2({
              userId: getCurrentUser()!.id,
              surveyId: 4,
            });

            if (rsp.code === 80005000) {
              await clearSurvey({ userId: getCurrentUser()!.id, surveyId: 4 });
            } else if (rsp.code === 200 && rsp.data!.hasRecord === false) {
              setSurveyIndex(4);
            } else if (differenceInDays(new Date().toISOString(), (rsp.data!.submitTime! + 'Z')) > 7) {
              rsp = await querySurveyV2({
                userId: getCurrentUser()!.id,
                surveyId: 5,
              });
              if (rsp.code === 80005000) {
                await clearSurvey({ userId: getCurrentUser()!.id, surveyId: 5 });
              } else if (rsp.code === 200 && rsp.data!.hasRecord === false) {
                setSurveyIndex(5);
              }
            }
          }
        }
      }
    }
    setup();
  }, [getCurrentUser]);

  return (
    <ContentLayout title={t("student.overview")} className="md:h-full md:overflow-hidden">
      {/* lg/xl 左 padding 对齐 2xl 的 pl-0(内容贴近侧栏,gap≈24)；2xl/1920 不变 */}
      <div
        className="flex flex-col gap-6 overflow-visible px-6 pb-6 pt-6 lg:pl-0 lg:pr-6 2xl:pl-0 2xl:pr-6 min-[1920px]:px-6 md:flex-1 md:min-h-0 md:overflow-y-auto [@media(max-height:1000px)]:gap-3! [@media(max-height:1000px)]:pt-3! [@media(max-height:1000px)]:pb-3!"
      >
        <section
          className="overflow-visible rounded-[50px] p-6 lg:p-8 2xl:min-h-[370px] 2xl:p-10 [@media(max-height:1000px)]:shrink-0 [@media(max-height:1000px)]:rounded-[28px]! [@media(max-height:1000px)]:p-4!"
          style={{ background: panelBg }}
        >
          {/* 面板標題：數學符號 glyph + 課業 */}
          <div className="mb-6 flex items-center gap-3 [@media(max-height:1000px)]:mb-2! [@media(max-height:1000px)]:gap-2!">
            <span
              className="grid grid-cols-2 gap-x-1 text-[20px] font-extrabold leading-none text-[#1A1C1D] [@media(max-height:1000px)]:text-base!"
              aria-hidden
            >
              <span>+</span>
              <span>−</span>
              <span>×</span>
              <span>÷</span>
            </span>
            <h2 className="text-2xl font-bold text-foreground md:text-3xl lg:text-4xl 2xl:text-[40px] [@media(max-height:1000px)]:text-2xl!">
              {t("common.homework")}
            </h2>
          </div>

          <div className="relative px-10 2xl:px-14">
            {carouselData.length > 0 && (
              <button
                type="button"
                onClick={() => carApi?.scrollPrev()}
                aria-label="Previous"
                className="absolute left-0 top-1/2 z-10 flex size-9 -translate-y-1/2 items-center justify-center rounded-full text-[#1A1C1D] shadow-sm transition hover:brightness-95 2xl:size-10"
                style={{ backgroundColor: arrow }}
              >
                <ChevronLeft className="size-5" strokeWidth={2.5} />
              </button>
            )}
            {carouselData.length > 0 && (
              <button
                type="button"
                onClick={() => carApi?.scrollNext()}
                aria-label="Next"
                className="absolute right-0 top-1/2 z-10 flex size-9 -translate-y-1/2 items-center justify-center rounded-full text-[#1A1C1D] shadow-sm transition hover:brightness-95 2xl:size-10"
                style={{ backgroundColor: arrow }}
              >
                <ChevronRight className="size-5" strokeWidth={2.5} />
              </button>
            )}

            <Carousel
              opts={{
                align: "start",
              }}
              setApi={setCarApi}
              className={`w-full ${styles.carouselOverflowVisible}`}
            >
              <CarouselContent className={styles.carouselOverflowVisible}>
                {carouselData.map((data: CarouselDataType, index: number) => {
                const percent =
                  data.total > 0 ? Math.round((data.done / data.total) * 100) : 0;
                return (
                  <CarouselItem key={index} className="w-fit basis-auto pl-4">
                    <NavLink to={ROUTE.STUDENT.HOMEWORK} state={data.assignments}>
                      <Card className="flex aspect-[204/209] w-[clamp(160px,calc(5.625vw+96px),240px)] flex-col rounded-[30px] border-0 bg-white px-[13%] pt-[13%] shadow-[0_8px_24px_-14px_rgba(0,0,0,0.25)] transition hover:-translate-y-0.5 hover:shadow-[0_14px_30px_-14px_rgba(0,0,0,0.3)] [@media(max-height:1000px)]:w-[clamp(124px,16vw,148px)]! [@media(max-height:1000px)]:rounded-[22px]!">
                        <CardContent className="flex h-full flex-col p-0">
                          {/* TOP: 題型序號 label + 題型名稱 */}
                          <div className="min-w-0">
                            {/* 題型序號 label — slate-400，1920 下 16px */}
                            <div className="line-clamp-1 text-[14px] font-medium leading-none text-[#94A3B8] 2xl:text-[16px] [@media(max-height:1000px)]:text-[12px]!">
                              {t("student.questype")} {String(data.ordinal).padStart(2, "0")}
                            </div>
                            {/* 題型名稱 — bold，1920 下 24px；与 label 间距 ~17px(11%)；单行省略(truncate,长词也出省略号),保证各卡环 Y 对齐(图2 单行设计) */}
                            <div className="mt-[11%] truncate text-[20px] font-bold leading-tight text-[#1A1C1D] xl:text-[22px] min-[1920px]:text-[24px]! [@media(max-height:1000px)]:mt-[6%]!">
                              {data.title}
                            </div>
                          </div>

                          {/* progress ring — 与名称间距 ~33px(22% of 内容宽,图2)，直径 ~76px(50%)，随卡宽缩放 */}
                          <div className="mt-[22%] aspect-square w-[50%] [@media(max-height:1000px)]:mt-[8%]!">
                            <ProgressRing percent={percent} color={accent} />
                          </div>
                        </CardContent>
                      </Card>
                    </NavLink>
                  </CarouselItem>
                );
              })}
              </CarouselContent>
            </Carousel>
          </div>
        </section>

        <OverviewAgenda
          events={calendarData}
          accent={accent}
          arrow={arrow}
          isEnglish={isEN(i18n)}
          calendarLabel={t("common.calendar")}
          // md+ 撑到剩余高度: 与课业面板组成 flex-col, 日程表底部与左侧菜单栏底部对齐
          className="md:flex-1 md:min-h-0"
          renderTooltip={(event): ReactNode => {
            const model = event.data as AssignmentModel | undefined;
            const createdAt = model?.assignment.createdAt;
            const dueDate = model?.assignment.dueDate;
            return (
              <div className="flex flex-col gap-1.5 text-sm">
                <span className="font-semibold text-[#1A1C1D]">
                  {event.title}
                </span>
                {createdAt && (
                  <span className="text-[#64748B]">
                    {t("common.publish")}: {format(new Date(createdAt), "yyyy/MM/dd")}
                  </span>
                )}
                {dueDate && (
                  <span className="text-[#64748B]">
                    {t("common.due")}: {format(new Date(dueDate), "yyyy/MM/dd")}
                  </span>
                )}
                {model && (
                  <NavLink
                    to={ROUTE.STUDENT.HOMEWORK}
                    state={[model]}
                    className="mt-0.5 font-semibold text-[color:var(--sidebar-active)] hover:underline"
                  >
                    {t("student.goreview")}
                  </NavLink>
                )}
              </div>
            );
          }}
        />
      </div>

      {surveyIndex === 1 && (
        <Survey
          onSuccess={() => {
            setSurveyIndex(2);
          }}
        ></Survey>

      )}

      {surveyIndex === 2 && (
        <Survey2 onSuccess={() => {
          setSurveyIndex(undefined);
        }}></Survey2>
      )}

      {surveyIndex === 3 && (
        <Survey3
          onSuccess={() => {
            setSurveyIndex(4);
          }}
        ></Survey3>
      )}

      {surveyIndex === 4 && (
        <Survey4 onSuccess={() => {
          setSurveyIndex(undefined);
        }}></Survey4>
      )}

      {surveyIndex === 5 && (
        <Survey5 onSuccess={() => {
          setSurveyIndex(undefined);
        }}></Survey5>
      )}
    </ContentLayout>
  );
}

export default withTranslation()(OverviewPage);
